import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { bindTelegramBackButton } from "../../telegram/webapp";
import {
  ALL_PRICE_BY_CATEGORY_TOKEN,
  categorySelectionKeys,
  filterCategoryOptions,
  isWholePriceByCategory,
  type CategoryOption
} from "./category-model";

export type CategorySelectFieldProps = {
  options: CategoryOption[];
  value: string[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  onApply: (keys: string[]) => void;
};

export type CategoryPickerProps = {
  open: boolean;
  options: CategoryOption[];
  value: string[];
  catalogUnavailable?: boolean;
  onApply: (keys: string[]) => void;
  onClose: () => void;
};

const focusableSelector = "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

const categorySummary = (value: string[], loading: boolean, error: boolean) => {
  if (isWholePriceByCategory(value)) return "Категории: весь прайс по категориям";
  const count = categorySelectionKeys(value).length;
  if (error) return count === 0 ? "Категории: весь прайс · каталог недоступен" : `Категории: выбрано ${count} · каталог недоступен`;
  if (loading) return "Категории: загрузка";
  return count === 0 ? "Категории: весь прайс" : `Категории: выбрано ${count}`;
};

const selectionContext = (keys: string[]) => {
  if (isWholePriceByCategory(keys)) return "Весь прайс по категориям";
  return keys.length === 0 ? "Весь прайс" : `Выбрано: ${keys.length}`;
};

export function CategorySelectField({ options, value, loading, error, onRetry, onApply }: CategorySelectFieldProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  const removableCount = categorySelectionKeys(value)
    .filter((key) => key !== ALL_PRICE_BY_CATEGORY_TOKEN).length;
  const close = () => {
    restoreFocusRef.current = true;
    setOpen(false);
  };
  const apply = (keys: string[]) => {
    onApply(keys);
    close();
  };

  useEffect(() => {
    if (open || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    const frame = requestAnimationFrame(() => triggerRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  return <div className="category-select-field">
    <button ref={triggerRef} type="button" className="category-select-trigger" disabled={loading || (error && removableCount === 0)} onClick={() => setOpen(true)}>
      {categorySummary(value, loading, error)}
    </button>
    {error && <button type="button" className="category-retry" onClick={onRetry}>Повторить загрузку категорий</button>}
    <CategoryPicker open={open} options={options} value={value} catalogUnavailable={error} onApply={apply} onClose={close} />
  </div>;
}

export function CategoryPicker({ open, options, value, catalogUnavailable = false, onApply, onClose }: CategoryPickerProps) {
  const [query, setQuery] = useState("");
  const [temporaryKeys, setTemporaryKeys] = useState<string[]>(() => categorySelectionKeys(value));
  const searchRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setQuery("");
    setTemporaryKeys(categorySelectionKeys(value));
  }, [open, value]);

  const close = useCallback(() => {
    setTemporaryKeys(categorySelectionKeys(value));
    onClose();
  }, [onClose, value]);

  useEffect(() => {
    if (!open) return;
    (searchRef.current ?? dialogRef.current?.querySelector<HTMLElement>(focusableSelector))?.focus();
    const body = document.body;
    const backdrop = backdropRef.current;
    const originalOverflow = body.style.overflow;
    const background = Array.from(body.children).filter((element) => element !== backdrop);
    const originalBackground = background.map((element) => ({
      element,
      inert: element.hasAttribute("inert"),
      ariaHidden: element.getAttribute("aria-hidden")
    }));
    body.style.overflow = "hidden";
    background.forEach((element) => {
      element.setAttribute("inert", "");
      element.setAttribute("aria-hidden", "true");
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? []);
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const unbind = bindTelegramBackButton(close);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      unbind();
      body.style.overflow = originalOverflow;
      originalBackground.forEach(({ element, inert, ariaHidden }) => {
        if (inert) element.setAttribute("inert", "");
        else element.removeAttribute("inert");
        if (ariaHidden === null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", ariaHidden);
      });
    };
  }, [close, open]);

  const { available, missing } = useMemo(() => {
    const selected = new Set(temporaryKeys);
    if (catalogUnavailable) {
      const sourceByKey = new Map(options.map((option) => [option.key, option]));
      return {
        available: [],
        missing: temporaryKeys
          .filter((key) => key !== ALL_PRICE_BY_CATEGORY_TOKEN)
          .map((key) => sourceByKey.get(key) ?? { key, label: key, itemCount: 0, available: false })
      };
    }
    const shown = filterCategoryOptions(options.filter((option) => option.available), query);
    return {
      available: [
        ...shown.filter((option) => selected.has(option.key)),
        ...shown.filter((option) => !selected.has(option.key))
      ],
      missing: options.filter((option) => !option.available && selected.has(option.key))
    };
  }, [catalogUnavailable, options, query, temporaryKeys]);

  const toggle = (key: string) => setTemporaryKeys((keys) => {
    const selected = categorySelectionKeys(keys).filter((item) => item !== ALL_PRICE_BY_CATEGORY_TOKEN);
    return selected.includes(key)
      ? selected.filter((item) => item !== key)
      : [...selected, key];
  });
  const remove = (key: string) => setTemporaryKeys((keys) => keys.filter((item) => item !== key));
  const failureWouldBroaden = catalogUnavailable
    && categorySelectionKeys(value).some((key) => key !== ALL_PRICE_BY_CATEGORY_TOKEN)
    && temporaryKeys.filter((key) => key !== ALL_PRICE_BY_CATEGORY_TOKEN).length === 0;
  const orderedTemporaryKeys = () => {
    const selected = categorySelectionKeys(temporaryKeys);
    if (isWholePriceByCategory(selected)) return selected;
    const selectedSet = new Set(selected);
    const listed = options.filter((option) => selectedSet.has(option.key)).map((option) => option.key);
    const listedSet = new Set(listed);
    return [...listed, ...selected.filter((key) => !listedSet.has(key))];
  };
  const apply = () => {
    if (failureWouldBroaden) return;
    const keys = catalogUnavailable
      ? categorySelectionKeys(value).filter((key) => temporaryKeys.includes(key))
      : orderedTemporaryKeys();
    onApply(keys);
  };

  if (!open || typeof document === "undefined") return null;
  return createPortal(<div ref={backdropRef} className="category-picker-backdrop" data-testid="category-picker-backdrop" onClick={(event) => {
    if (event.target === event.currentTarget) close();
  }}>
    <section ref={dialogRef} className="category-picker" role="dialog" aria-modal="true" aria-labelledby="category-picker-title">
      <header className="category-picker-header">
        <div><p className="eyebrow">Исходный прайс</p><h2 id="category-picker-title">Выбор категорий</h2><p className="category-picker-count" aria-live="polite">{selectionContext(temporaryKeys)}</p></div>
        <button type="button" aria-label="Закрыть выбор категорий" onClick={close}>×</button>
      </header>
      {!catalogUnavailable && <label className="search-field"><span aria-hidden="true">⌕</span><input ref={searchRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск категорий" aria-label="Поиск категорий" /></label>}
      {!catalogUnavailable && <div className="category-picker-all-actions">
        <button type="button" className="category-all-price" onClick={() => setTemporaryKeys([])}>Весь прайс</button>
      </div>}
      <div className="category-picker-content">
        {available.length > 0 && <div className="category-picker-list">
          {available.map((option) => <label className="category-picker-row" key={option.key}>
            <input type="checkbox" checked={temporaryKeys.includes(option.key)} onChange={() => toggle(option.key)} aria-label={option.label} />
            <span><strong>{option.label}</strong><small>{option.itemCount} товаров</small></span>
          </label>)}
        </div>}
        {!catalogUnavailable && available.length === 0 && <p className="category-empty">Ничего не найдено.</p>}
        {missing.length > 0 && <section className="category-missing"><h3>{catalogUnavailable ? "Сохранённые категории" : "Больше нет в прайсе"}</h3>{missing.map((option) => <div key={option.key}><span>{option.label}</span><button type="button" onClick={() => remove(option.key)} aria-label={`Убрать категорию ${option.label}`}>Убрать</button></div>)}</section>}
        {failureWouldBroaden && <p className="category-failure-note" role="alert">Повторите загрузку каталога, прежде чем выбирать весь прайс.</p>}
      </div>
      <footer className="category-picker-footer"><button type="button" onClick={close}>Отмена</button><button type="button" className="primary-button" disabled={failureWouldBroaden} onClick={apply}>Готово · {temporaryKeys.length}</button></footer>
    </section>
  </div>, document.body);
}
